import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CURRENT_BUILD,
  VERSION_CHECK_INTERVAL_MS,
  fetchLatestBuild,
  hasAutoReloaded,
  isNewerBuild,
  isSafeAutoReloadPath,
  markAutoReloaded,
} from '../appVersion';

function AppUpdateNotifier({
  currentBuild = CURRENT_BUILD,
  fetchBuild = fetchLatestBuild,
  reload = () => window.location.reload(),
}) {
  const location = useLocation();
  const [latestBuild, setLatestBuild] = useState(null);

  const checkForUpdate = useCallback(async () => {
    try {
      const latest = await fetchBuild();
      if (isNewerBuild(latest, currentBuild)) setLatestBuild(latest);
    } catch (_) {
      // A failed update check must never block the application itself.
    }
  }, [currentBuild, fetchBuild]);

  useEffect(() => {
    checkForUpdate();
    const interval = window.setInterval(checkForUpdate, VERSION_CHECK_INTERVAL_MS);
    const onFocus = () => checkForUpdate();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkForUpdate]);

  useEffect(() => {
    if (!latestBuild || !isSafeAutoReloadPath(location.pathname)) return;
    if (hasAutoReloaded(latestBuild.version)) return;
    markAutoReloaded(latestBuild.version);
    reload();
  }, [latestBuild, location.pathname, reload]);

  if (!latestBuild) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        zIndex: 2147483647,
        left: 'max(12px, env(safe-area-inset-left))',
        right: 'max(12px, env(safe-area-inset-right))',
        top: 'max(12px, env(safe-area-inset-top))',
        maxWidth: 560,
        margin: '0 auto',
        padding: '12px 14px',
        borderRadius: 12,
        background: '#1A1614',
        color: '#fff',
        boxShadow: '0 12px 32px rgba(0,0,0,.24)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        fontFamily: '"Inter", "Noto Sans SC", -apple-system, sans-serif',
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <span>发现新版本，刷新后即可使用最新功能。</span>
      <button
        type="button"
        onClick={reload}
        style={{
          flex: '0 0 auto',
          border: 0,
          borderRadius: 9,
          padding: '9px 13px',
          background: '#F777B0',
          color: '#fff',
          font: 'inherit',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        立即刷新
      </button>
    </div>
  );
}

export default AppUpdateNotifier;
